import type { Channel } from '@ably/laravel-echo'

import { onBeforeUnmount } from 'vue'

interface EventCallback<T = unknown> {
    (event: T): void
}

interface ChannelData {
    eventCallbacks: Record<string, Array<EventCallback>>
    privateChannel: Channel
}

const channelStore: Record<string, ChannelData> = {}

export function usePrivateChannel(channelName: string) {
    if (!window.Echo) {
        return {
            listen: () => {},
        }
    }

    if (!channelStore[channelName]) {
        const privateChannel = window.Echo.private(channelName)
        channelStore[channelName] = {
            eventCallbacks: {},
            privateChannel,
        }
    }

    const { eventCallbacks, privateChannel } = channelStore[channelName]

    const addedCallbacks: Array<{ eventName: string; callback: (event: unknown) => void }> = []
    function listen<T>(eventName: string, callback: (event: T) => void) {
        if (!eventCallbacks[eventName]) {
            eventCallbacks[eventName] = []

            privateChannel.listen(eventName, (event: T) => {
                eventCallbacks[eventName]?.forEach((callback) => callback(event))
            })
        }

        const typedCallback = callback as EventCallback
        eventCallbacks[eventName].push(typedCallback)
        addedCallbacks.push({ eventName, callback: typedCallback })
    }

    onBeforeUnmount(() => {
        for (const { eventName, callback } of addedCallbacks) {
            const callbacks = eventCallbacks[eventName]
            if (!callbacks) continue

            const index = callbacks.indexOf(callback)
            if (index !== -1) callbacks.splice(index, 1)

            // We'll stop listening to the event if there are no more active events
            if (callbacks.length === 0) {
                privateChannel.stopListening(eventName)
                delete eventCallbacks[eventName]
            }

            // We'll leave the channel if there are no more active events for the channel
            if (Object.keys(eventCallbacks).length === 0) {
                window.Echo.leave(channelName)
            }
        }
    })

    return {
        listen,
    }
}
